import { inject, Injectable } from '@angular/core';
import { TasksDataService } from '@app/shared/services/tasks-data.service';
import { AbsenceReport, isAbsenceReport } from '@app/shared/types/models/reports/absence-report';
import { TimeReportExcelRow } from '@app/shared/types/models/reports/time-report-excel-row.model';
import { TimeReportRowVM } from '@app/shared/types/models/reports/time-report-row.model';
import { isWorkTimeReport, WorkTimeReport } from '@app/shared/types/models/reports/work-time-report';
import { User } from '@app/shared/types/models/user/user.model';

/**
 * TimeReportExcelRowMapper - Maps mixed Reports rows to Excel source values.
 *
 * Preserves the existing WorkTime Excel field contract. Absence fills shared
 * fields and leaves WorkTime-only fields blank. Does not build a workbook,
 * choose columns, or format Date / Hours / Month / Week / decimal hours.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Phase 6C Excel export. Task links are resolved from {@link TasksDataService}
 * (in-memory store only, no HTTP).
 */
@Injectable({ providedIn: 'root' })
export class TimeReportExcelRowMapper {
  private readonly tasksDataService = inject(TasksDataService);

  public map(row: TimeReportRowVM): TimeReportExcelRow {
    if (isWorkTimeReport(row.source)) {
      return this.mapWorkTime(row, row.source);
    }
    if (isAbsenceReport(row.source)) {
      return this.mapAbsence(row, row.source);
    }
    return this.mapUnknown(row);
  }

  public mapMany(rows: TimeReportRowVM[]): TimeReportExcelRow[] {
    return rows.map(item => this.map(item));
  }

  private mapWorkTime(row: TimeReportRowVM, report: WorkTimeReport): TimeReportExcelRow {
    const user = report.user?.();
    const estimationTime = report.task?.estimation?.time;

    return {
      kind: row.kind,
      date: report.date,
      user: this.formatWorkTimeUser(user),
      company: row.companyName ?? '',
      project: report.project?.name || '',
      taskId: this.formatTaskIdLabel(report),
      taskLink: this.resolveTaskUrl(report.taskId ?? report.task?.id),
      taskTitle: report.task?.name || '',
      location: this.formatLocation(report),
      description: report.description || '',
      type: report.hoursType || '',
      duration: report.duration ?? '',
      estimation: estimationTime ? estimationTime : '',
      status: report.status || ''
    };
  }

  private mapAbsence(row: TimeReportRowVM, report: AbsenceReport): TimeReportExcelRow {
    return {
      kind: row.kind,
      date: row.date,
      user: row.userName,
      company: row.companyName ?? '',
      project: '',
      taskId: '',
      taskLink: '',
      taskTitle: '',
      location: '',
      description: row.description ?? '',
      type: report.absenceType || '',
      duration: row.duration ?? '',
      estimation: '',
      status: row.status || ''
    };
  }

  private mapUnknown(row: TimeReportRowVM): TimeReportExcelRow {
    return {
      kind: row.kind,
      date: row.date,
      user: row.userName,
      company: row.companyName ?? '',
      project: '',
      taskId: '',
      taskLink: '',
      taskTitle: '',
      location: '',
      description: row.description ?? '',
      type: '',
      duration: row.duration ?? '',
      estimation: '',
      status: row.status || ''
    };
  }

  private formatWorkTimeUser(user: User | undefined): string {
    if (!user) {
      return '';
    }
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }

  private formatTaskIdLabel(report: WorkTimeReport): string {
    if (!report.task?.taskNumber) {
      return '';
    }

    return `${report.task.taskType?.code}-${report.task.taskNumber}`;
  }

  private formatLocation(report: WorkTimeReport): string {
    const locationName = report.location?.name;
    return Array.isArray(locationName) ? locationName.join(', ') : locationName || '';
  }

  private resolveTaskUrl(taskId: string | undefined): string {
    if (!taskId) {
      return '';
    }

    const task = this.tasksDataService.getById(taskId)();
    return task?.url() ?? '';
  }
}
